/**
 * Bundle Optimization Utilities
 * Provides utilities for optimizing bundle size and implementing code splitting
 */

// Dynamic imports for better code splitting
export const loadComponent = async <T = any>(
  importFunction: () => Promise<{ default: T }>,
  fallback?: T
): Promise<T> => {
  try {
    const module = await importFunction();
    return module.default;
  } catch (error) {
    console.warn('Failed to load component:', error);
    if (fallback) {
      return fallback;
    }
    throw error;
  }
};

// Preload components that are likely to be needed
export const preloadComponents = async () => {
  // Preload critical components
  try {
    await Promise.all([
      loadComponent(() => import('../components/LazyImage')),
      loadComponent(() => import('../components/LoadingSpinner'))
    ]);
  } catch (error) {
    console.warn('Failed to preload some components:', error);
  }
};

// Lazy load heavy libraries (only include libraries that are actually installed)
export const loadHeavyLibraries = {
  // Chart libraries (if recharts is installed)
  // charts: () => import('recharts'),
  
  // Date libraries (if date-fns is installed)
  // dateFns: () => import('date-fns'),
  
  // Utility libraries (if lodash-es is installed)
  // lodash: () => import('lodash-es'),
  
  // Animation libraries (if framer-motion is installed)
  // framerMotion: () => import('framer-motion'),
  
  // PDF libraries (if pdfjs-dist is installed)
  // pdfjs: () => import('pdfjs-dist'),
  
  // Rich text editors (if quill is installed)
  // quill: () => import('quill')
};

// Bundle analyzer helper
export const analyzeBundle = () => {
  if (process.env.NODE_ENV === 'development') {
    // This would integrate with webpack-bundle-analyzer
    console.log('Bundle analysis available in development mode');
  }
};

// Tree shaking optimization (only include libraries that are actually installed)
export const optimizeImports = {
  // Import only what you need from large libraries (if installed)
  // lodash: {
  //   debounce: () => import('lodash-es/debounce'),
  //   throttle: () => import('lodash-es/throttle'),
  //   isEmpty: () => import('lodash-es/isEmpty'),
  //   isEqual: () => import('lodash-es/isEqual')
  // },
  
  // Date-fns tree shaking (if installed)
  // dateFns: {
  //   format: () => import('date-fns/format'),
  //   parseISO: () => import('date-fns/parseISO'),
  //   addDays: () => import('date-fns/addDays'),
  //   differenceInDays: () => import('date-fns/differenceInDays')
  // },
  
  // Lucide React tree shaking (if needed)
  // lucide: {
  //   icons: () => import('lucide-react/dist/esm/icons')
  // }
};

// Code splitting strategies
export const codeSplittingStrategies = {
  // Route-based splitting (already implemented in App.tsx)
  routeBased: true,
  
  // Component-based splitting
  componentBased: true,
  
  // Library-based splitting
  libraryBased: true,
  
  // Feature-based splitting
  featureBased: true
};

// Performance budgets
export const performanceBudgets = {
  // JavaScript bundle size limits
  js: {
    initial: 250 * 1024, // 250KB
    total: 1000 * 1024,  // 1MB
    perRoute: 100 * 1024 // 100KB per route
  },
  
  // CSS bundle size limits
  css: {
    initial: 50 * 1024,  // 50KB
    total: 200 * 1024    // 200KB
  },
  
  // Image size limits
  images: {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 80
  }
};

// Bundle size monitoring
export const monitorBundleSize = () => {
  if (typeof window !== 'undefined' && 'performance' in window) {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (navigation) {
      const transferSize = navigation.transferSize;
      const encodedBodySize = navigation.encodedBodySize;
      const decodedBodySize = navigation.decodedBodySize;
      
      console.log('Bundle size metrics:', {
        transferSize: `${(transferSize / 1024).toFixed(2)} KB`,
        encodedBodySize: `${(encodedBodySize / 1024).toFixed(2)} KB`,
        decodedBodySize: `${(decodedBodySize / 1024).toFixed(2)} KB`,
        compressionRatio: `${((1 - encodedBodySize / decodedBodySize) * 100).toFixed(1)}%`
      });
      
      // Check against performance budgets
      if (transferSize > performanceBudgets.js.initial) {
        console.warn('Initial bundle size exceeds budget:', {
          actual: `${(transferSize / 1024).toFixed(2)} KB`,
          budget: `${(performanceBudgets.js.initial / 1024).toFixed(2)} KB`
        });
      }
    }
  }
};

// Resource hints for better loading
export const addResourceHints = () => {
  // DNS prefetch for external domains
  const dnsPrefetchDomains = [
    'fonts.googleapis.com',
    'fonts.gstatic.com',
    'cdn.jsdelivr.net'
  ];
  
  dnsPrefetchDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = `//${domain}`;
    document.head.appendChild(link);
  });
  
  // Preconnect to critical origins
  const preconnectOrigins = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com'
  ];
  
  preconnectOrigins.forEach(origin => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};

// Critical CSS extraction
export const extractCriticalCSS = () => {
  // This would be implemented with a tool like critical
  console.log('Critical CSS extraction would be implemented here');
};

// Service worker for caching
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw-offline.js');
      console.log('Service worker registered:', registration);
      
      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content is available
              console.log('New content is available, please refresh');
            }
          });
        }
      });
    } catch (error) {
      console.error('Service worker registration failed:', error);
    }
  }
};

// Initialize bundle optimizations
export const initBundleOptimizations = () => {
  // Add resource hints
  addResourceHints();
  
  // Preload critical components
  preloadComponents();
  
  // Monitor bundle size
  monitorBundleSize();
  
  // Register service worker
  registerServiceWorker();
  
  // Analyze bundle in development
  if (process.env.NODE_ENV === 'development') {
    analyzeBundle();
  }
};

export default {
  loadComponent,
  preloadComponents,
  loadHeavyLibraries,
  analyzeBundle,
  optimizeImports,
  codeSplittingStrategies,
  performanceBudgets,
  monitorBundleSize,
  addResourceHints,
  extractCriticalCSS,
  registerServiceWorker,
  initBundleOptimizations
};
